import React, { useCallback, useEffect, useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { BUTTON_GHOST } from '../../components/portal/adminStyles';
import { CallSheetCreate } from '../../components/portal/CallSheetCreate';
import { CallSheetRead } from '../../components/portal/CallSheetRead';
import { CallSheetBuilder } from './CallSheetBuilder';
import { useIsPhone } from '../../utils/isPhone';
import { useOpenIdHistory } from '../../utils/studioViewHistory';
import { callSheetDateLabel, callSheetName, fetchCallSheetTargets, fetchCallSheets, todayISO, type CallSheetSummary, type CallSheetTargets } from '../../utils/callSheets';

/**
 * The Call Sheets tab (13 Sep 2026).
 *
 * Call sheets used to be reachable only from inside a project, one apiece.
 * Tom hit the wall that creates: ATM is a single production with a build day,
 * a tech test and four live days, and a second call sheet meant inventing a
 * second project for a live TV show that has no deliverables in it.
 *
 * So this is a studio-wide list, and the thing it lists is a DAY. Anything
 * still ahead sits at the top, soonest first; everything past runs underneath
 * it, newest first — the question this page answers on a Sunday night is
 * "what's out this week", not "what did we shoot in July".
 *
 * **24 Sep 2026: on a phone, opening one opens it to READ.** The builder is
 * a form and a form is the wrong thing to hand somebody in a car park at 6am;
 * CallSheetRead answers what time, where, and who do I ring, with every
 * number dialling. Edit puts the builder back. On a laptop nothing changed.
 *
 * **COMING UP IS ALWAYS DRAWN (16 Oct 2026).** It used to render only when
 * it had rows, so with nothing booked the whole page was a single heading
 * reading PAST — an archive, when the only question anyone brings to a call
 * sheet list is what is next. An empty section that says it is empty answers
 * that question; a missing section does not answer it at all.
 */

const EMPTY_TARGETS: CallSheetTargets = {
  clients: [],
  shows: [],
  projects: []
};

/**
 * Where this sheet is attached, with the stutter taken out.
 *
 * `callSheetContext` in utils already drops exactly repeated parts, but it
 * compares the strings as typed — so ATM's show "ATM TV Live" and its
 * project "ATM TV LIVE" survived as two parts and the row printed the name
 * twice. It also had no way to know the row's own heading, so a sheet
 * titled after its project read ECONOMY OF SCALE 03 and then, two columns
 * later, · ECONOMY OF SCALE 03.
 *
 * Here the row knows its own title, so both go: compare folded, and drop
 * anything that is already the heading.
 */
function rowContext(sheet: CallSheetSummary, heading: string): string {
  const fold = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();
  const seen = new Set<string>([fold(heading)]);
  const parts: string[] = [];
  for (const part of [sheet.clientName, sheet.showName, sheet.projectName]) {
    const value = (part ?? '').trim();
    if (!value || seen.has(fold(value))) continue;
    seen.add(fold(value));
    parts.push(value);
  }
  return parts.join('  ·  ');
}
function Row({
  sheet,
  index,
  onOpen
}: {
  sheet: CallSheetSummary;
  index: number;
  onOpen: () => void;
}) {
  const heading = callSheetName(sheet);
  const context = rowContext(sheet, heading);
  return <li className="row-in" style={{
    ['--row' as string]: Math.min(index, 10)
  }} data-id="element-2344" data-mp="2qt84i3t7rcpk">
      <button type="button" onClick={onOpen} className="group flex w-full flex-wrap items-baseline gap-x-8 gap-y-2 border-b border-paper/10 py-6 text-left transition-colors duration-150 ease-out hover:border-paper/40" data-id="element-2345" data-mp="2346re39frddy">
        <span className="label-sm min-w-[9.5rem] shrink-0 text-paper/60" data-id="element-2346" data-mp="2cyyt9ja1wmej">
          {callSheetDateLabel(sheet.shootDate)}
        </span>
        <span className="min-w-[12rem] flex-1 font-display text-xl leading-tight text-paper transition-colors duration-150 ease-out group-hover:text-paper" data-id="element-2347" data-mp="3ofp116u4hma9">
          {heading}
        </span>
        <span className="label-sm max-w-full text-paper/60" data-id="element-2348" data-mp="1t11qxevha8nf">{context}</span>
        {/* A count is desk information; on a phone it is one more thing
            between you and the sheet. It also has to say WHAT it counts —
            "8 items" left you to guess between crew, kit and schedule. */}
        <span className="label-sm hidden shrink-0 text-paper/40 sm:inline" data-id="element-2349" data-mp="270b7ifnlj4rx">
          {sheet.scheduleCount === 0 ? 'No schedule yet' : `${sheet.scheduleCount} on the schedule`}
        </span>
      </button>
    </li>;
}
export function CallSheets() {
  const [sheets, setSheets] = useState<CallSheetSummary[]>([]);
  const [targets, setTargets] = useState<CallSheetTargets>(EMPTY_TARGETS);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  /** Phone only: the read view is the default, and this is the way past it. */
  const [editing, setEditing] = useState(false);
  const isPhone = useIsPhone();

  /* Which sheet is open is a screen, so it belongs in browser history too —
     otherwise back from the builder skips the list and leaves Call Sheets
     altogether, which is the same complaint one level down. */
  useOpenIdHistory('callSheetId', openId, setOpenId);

  /* Closing a sheet must not leave "editing" armed for the next one you open. */
  useEffect(() => {
    if (!openId) setEditing(false);
  }, [openId]);
  const load = useCallback(async () => {
    setLoading(true);
    const [rows, options] = await Promise.all([fetchCallSheets(), fetchCallSheetTargets()]);
    setSheets(rows);
    setTargets(options);
    setLoading(false);
  }, []);

  /* Reloads whenever the list is what's on screen — on arrival, and on the
     way back from a sheet however you got back, the browser's button
     included. A sheet you have just retitled must not still show its old
     name in the row behind it. */
  useEffect(() => {
    if (!openId) void load();
  }, [openId, load]);

  // Opening one replaces this page entirely rather than sitting beside it:
  // the builder is long, and two scrolling contexts stacked on one screen is
  // how you lose your place on a shoot morning.
  if (openId) {
    if (isPhone && !editing) {
      return <CallSheetRead callSheetId={openId} onBack={() => setOpenId(null)} onEdit={() => setEditing(true)} data-id="element-2350" data-mp="1zudxgbn8wu9q" />;
    }
    return <CallSheetBuilder callSheetId={openId}
    /* On a phone, back out of the builder lands on the sheet you were
       reading rather than skipping it to the list — the same rule the
       history hook applies one level up. */ onBack={() => isPhone && editing ? setEditing(false) : setOpenId(null)} data-id="element-2351" data-mp="1lwz3ctm1rybf" />;
  }
  const today = todayISO();
  const upcoming = sheets.filter(sheet => !sheet.shootDate || sheet.shootDate >= today);
  const past = sheets.filter(sheet => sheet.shootDate && sheet.shootDate < today);
  /* The blank slate has its own panel below; an empty Coming Up on top of it
     would be the same news twice. */
  const blank = sheets.length === 0;
  return <div data-id="element-2352" data-mp="19ca8563jhp2x">
      <header className="flex flex-wrap items-end justify-between gap-6" data-id="element-2353" data-mp="19c0hdgao7dw6">
        <h1 className="font-display text-4xl leading-[0.95] tracking-tight sm:text-6xl" data-id="element-2354" data-mp="19ed56a340ih5">
          Call sheets
        </h1>
        {!creating && <button type="button" onClick={() => setCreating(true)} className="label inline-flex h-11 items-center gap-3 border border-paper/30 px-6 text-paper/70 transition-colors duration-150 ease-out hover:border-paper hover:text-paper" data-id="element-2355" data-mp="1ic6zxgl87427">
            <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-2356" data-mp="1nbjr2dj4qxal" />
            New Call Sheet
          </button>}
      </header>

      {creating && <CallSheetCreate targets={targets} onCancel={() => setCreating(false)} onCreated={id => {
      setCreating(false);
      /* A sheet you just made is empty, so it opens to the builder
         whatever you are holding. */
      setEditing(true);
      setOpenId(id);
    }} data-id="element-2357" data-mp="3u4luxjv1psed" />}

      {loading && <p className="label mt-12 text-paper/35" data-id="element-2358" data-mp="3d0kbrb8wa0m0">Loading…</p>}

      {!loading && blank && !creating && <div className="mt-12 max-w-xl border border-paper/25 px-6 py-8 sm:px-8" data-id="element-2359" data-mp="3hydic5e40wmt">
          <p className="font-display text-2xl leading-snug" data-id="element-2360" data-mp="2kcohed6sskse">No call sheets yet.</p>
          <p className="label-sm mt-4 leading-relaxed text-paper/60" data-id="element-2361" data-mp="2p9ogu5956paj">
            A call sheet is one day. Make one for a client — attach it to a project to pull in the
            crew and contacts, or leave it standalone for a live day that isn’t a filming project.
          </p>
          <button type="button" onClick={() => setCreating(true)} className={`${BUTTON_GHOST} mt-7`} data-id="element-2362" data-mp="9jv3nktnyiu8">
            New Call Sheet
          </button>
        </div>}

      {!loading && !blank && <section className="mt-12" data-id="element-2363" data-mp="1z0yzx4qj9xvk">
          <span className="label block text-paper/60" data-id="element-2364" data-mp="1yzxqf4p61jfy">Coming up</span>
          {upcoming.length > 0 ? <ul className="mt-5 border-t border-paper/10" data-id="element-2365" data-mp="1u25ywbsfnzca">
              {upcoming.map((sheet, index) => <Row key={sheet.id} sheet={sheet} index={index} onOpen={() => setOpenId(sheet.id)} data-id="element-2366" data-mp="1p8kykd3dat9x" data-mpi={index} />)}
            </ul> : <p className="mt-5 border-t border-paper/10 py-6 font-display text-xl text-paper/45" data-id="element-2367" data-mp="1bad5whdd6e4k">
              Nothing booked.
            </p>}
        </section>}

      {!loading && past.length > 0 && <section className="mt-14" data-id="element-2368" data-mp="11dqndz8ifz5p">
          <span className="label block text-paper/60" data-id="element-2369" data-mp="11etcc1m9vriz">Past</span>
          <ul className="mt-5 border-t border-paper/10 opacity-70" data-id="element-2370" data-mp="30orqqql9hgus">
            {past.map((sheet, index) => <Row key={sheet.id} sheet={sheet} index={index} onOpen={() => setOpenId(sheet.id)} data-id="element-2371" data-mp="2vr9f2sfv0nj3" data-mpi={index} />)}
          </ul>
        </section>}
    </div>;
}